import { Beat, type BeatId } from '../game/Onboarding';

/* ═══════════════════════════════════════════════════════════════════════════
   Le COACH — le rendu des cinq temps de l'onboarding, sans un mot.

   Un panneau translucide, posé au-dessus du jeu (pointer-events: none, il
   n'intercepte rien), qui montre un GLYPHE animé par temps et une rangée de
   pastilles de progression. Aucune phrase : des flèches pour « bouge », un
   réticule qui se resserre pour la hitbox, une balle qui frôle pour le graze,
   un éclat pour la bombe. Le langage est entièrement visuel — c'est la
   contrainte du jalon (PLAN.md §8).

   Ce fichier ne DÉCIDE rien : il reçoit un `Beat` de la machine `Onboarding` et
   l'affiche. Toute l'animation vit en CSS (`styles.css`, section coach) — ici
   on ne fait que poser le bon SVG et rejouer le fondu d'entrée à chaque
   changement de temps.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Glyphes SVG par temps. Palette volontairement HORS du rouge/rose/violet du
 *  danger (§4.5) : cyan pour l'action, or pour la récompense du graze. */
const GLYPHS: Record<number, string> = {
  // Bouger : une rose des directions, quatre chevrons qui respirent.
  [Beat.Move]: `<svg viewBox="0 0 100 100" class="cg cg-pulse" aria-hidden="true">
    <g fill="none" stroke="#22e0ff" stroke-width="7" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="50,10 40,22 60,22 50,10"/>
      <polyline points="50,90 40,78 60,78 50,90"/>
      <polyline points="10,50 22,40 22,60 10,50"/>
      <polyline points="90,50 78,40 78,60 90,50"/>
    </g>
    <circle cx="50" cy="50" r="6" fill="#22e0ff"/>
  </svg>`,
  // Hitbox : deux anneaux qui se resserrent sur le cœur, le seul point mortel.
  [Beat.Hitbox]: `<svg viewBox="0 0 100 100" aria-hidden="true">
    <circle cx="50" cy="50" r="34" fill="none" stroke="#22e0ff" stroke-width="4" class="cg-ring cg-ring-a"/>
    <circle cx="50" cy="50" r="22" fill="none" stroke="#22e0ff" stroke-width="4" class="cg-ring cg-ring-b"/>
    <circle cx="50" cy="50" r="7" fill="#eafcff" class="cg-core"/>
  </svg>`,
  // Graze : une balle qui rase le vaisseau, une gerbe d'étincelles à la clé.
  [Beat.Graze]: `<svg viewBox="0 0 100 100" aria-hidden="true">
    <circle cx="50" cy="58" r="8" fill="#eafcff"/>
    <path d="M20,20 Q50,44 30,80" fill="none" stroke="#ffd86b" stroke-width="5" stroke-linecap="round" class="cg-graze"/>
    <g fill="#ffd86b" class="cg-spark">
      <circle cx="42" cy="48" r="3"/><circle cx="58" cy="46" r="2.4"/><circle cx="50" cy="38" r="2"/>
    </g>
  </svg>`,
  // Bombe : des ondes qui balayent l'écran depuis le centre.
  [Beat.Bomb]: `<svg viewBox="0 0 100 100" aria-hidden="true">
    <circle cx="50" cy="50" r="12" fill="#22e0ff" class="cg-core"/>
    <circle cx="50" cy="50" r="20" fill="none" stroke="#22e0ff" stroke-width="4" class="cg-wave cg-wave-a"/>
    <circle cx="50" cy="50" r="20" fill="none" stroke="#22e0ff" stroke-width="4" class="cg-wave cg-wave-b"/>
  </svg>`,
};

export class CoachOverlay {
  private readonly root: HTMLElement;
  private readonly glyph: HTMLElement;
  private readonly pips: HTMLElement[] = [];
  private shown: BeatId | -1 = -1;

  constructor(parent: HTMLElement = document.body) {
    const root = document.createElement('div');
    root.id = 'coach';
    root.setAttribute('aria-hidden', 'true');
    root.style.display = 'none';

    const glyph = document.createElement('div');
    glyph.className = 'coach-glyph';
    root.appendChild(glyph);

    const pipRow = document.createElement('div');
    pipRow.className = 'coach-pips';
    // Quatre temps « actifs » (Done n'a pas de pastille) : la rangée montre où
    // l'on en est de l'apprentissage sans jamais l'écrire.
    for (let i = 0; i < 4; i++) {
      const pip = document.createElement('span');
      pip.className = 'coach-pip';
      pipRow.appendChild(pip);
      this.pips.push(pip);
    }
    root.appendChild(pipRow);

    parent.appendChild(root);
    this.root = root;
    this.glyph = glyph;
  }

  /** Affiche le temps donné. `Done` retire le panneau. No-op si le temps n'a pas
   *  changé — on ne retouche le DOM qu'aux transitions, jamais à 60 Hz. */
  show(beat: BeatId): void {
    if (beat === Beat.Done) {
      this.hide();
      return;
    }
    if (beat === this.shown) return;
    this.root.style.display = '';
    this.glyph.innerHTML = GLYPHS[beat] ?? '';
    for (let i = 0; i < this.pips.length; i++) {
      this.pips[i]!.classList.toggle('on', i <= beat);
    }
    // Rejoue le fondu d'entrée : on retire puis re-applique la classe après un
    // reflow forcé, sinon le navigateur ne redéclenche pas l'animation.
    this.root.classList.remove('coach-in');
    void this.root.offsetWidth;
    this.root.classList.add('coach-in');
    this.shown = beat;
  }

  hide(): void {
    this.root.style.display = 'none';
    this.shown = -1;
  }

  dispose(): void {
    this.root.remove();
  }
}
